import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { concat } from '@ember/helper';
import mult from 'madow-tech/helpers/mult';
import revealOnScroll from 'madow-tech/modifiers/reveal-on-scroll';
import Icon from 'madow-tech/components/icon';

interface Step {
  key: string;
  icon: string;
  animation: string;
}

export default class ProcessTimeline extends Component {
  steps: Step[] = [
    { key: 'discovery', icon: 'svg-search', animation: 'icon-pulse' },
    { key: 'design', icon: 'svg-brush', animation: 'icon-rotate' },
    { key: 'development', icon: 'svg-cpu', animation: 'icon-pulse' },
    { key: 'testing', icon: 'svg-flask', animation: 'icon-shield' },
    { key: 'launch', icon: 'svg-rocket', animation: 'icon-launch' },
  ];

  <template>
    <section class="py-16 md:py-20 px-4" id="process">
      <div class="mx-auto max-w-6xl">
        <h3
          class="text-2xl md:text-3xl font-bold mb-12 text-center"
          {{revealOnScroll animation="fade-up"}}
        >
          {{t "process.title"}}
        </h3>

        {{! Desktop: horizontal timeline }}
        <div class="hidden md:block relative">
          {{! Connecting line - animates from center outward }}
          <div
            class="absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-rose-500/50 via-purple-500/50 to-pink-500/50"
            {{revealOnScroll animation="draw-x-center" delay=200}}
          ></div>

          <div class="grid grid-cols-5 gap-4">
            {{#each this.steps as |step idx|}}
              <div
                class="flex flex-col items-center text-center relative"
                {{revealOnScroll animation="fade-up" delay=(mult idx 100)}}
              >
                {{! Step icon }}
                <div class="inline-flex items-center justify-center rounded-xl bg-mars-900/30 w-16 h-16 mb-4 text-rose-100 relative z-10">
                  <Icon
                    @name={{step.icon}}
                    @svgClass="h-9 w-9 {{step.animation}}"
                    class="!drop-shadow-rose-200/80 !drop-shadow-[0_0_5px]"
                  />
                </div>
                <h4 class="font-semibold text-white mb-2 display">
                  {{t (concat "process." step.key ".title")}}
                </h4>
                <p class="text-sm text-zinc-400 leading-relaxed">
                  {{t (concat "process." step.key ".body")}}
                </p>
              </div>
            {{/each}}
          </div>
        </div>

        {{! Mobile: vertical timeline }}
        <div class="md:hidden relative pl-4">
          {{! Vertical line - positioned relative to the icon center, animates from top }}
          <div
            class="absolute left-[calc(1rem+1.75rem)] top-0 bottom-0 w-0.5 bg-gradient-to-b from-rose-500/50 via-purple-500/50 to-pink-500/50"
            {{revealOnScroll animation="draw-y" delay=100}}
          ></div>

          <div class="space-y-8">
            {{#each this.steps as |step idx|}}
              <div
                class="flex items-start gap-4 relative"
                {{revealOnScroll animation="fade-up" delay=(mult idx 80)}}
              >
                {{! Step icon }}
                <div class="inline-flex items-center justify-center rounded-xl bg-mars-900/30 w-14 h-14 text-rose-100 flex-shrink-0 relative z-10">
                  <Icon
                    @name={{step.icon}}
                    @svgClass="h-8 w-8 {{step.animation}}"
                    class="!drop-shadow-rose-200/80 !drop-shadow-[0_0_5px]"
                  />
                </div>
                <div class="pt-2">
                  <h4 class="font-semibold text-white mb-1 display">
                    {{t (concat "process." step.key ".title")}}
                  </h4>
                  <p class="text-sm text-zinc-400 leading-relaxed">
                    {{t (concat "process." step.key ".body")}}
                  </p>
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      </div>
    </section>
  </template>
}
